import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ExternalLink, Network, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import apiConfig from 'constants/apiConfig';
import {
  filesOrFoldersKeys,
  useGetAllFiles,
  type NotebookFile,
} from 'components/FileManagement/hooks/useGetAllFiles';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import { fetchDownloadSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import { useRosterTemplateLibrary } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import {
  addSessionIncidentCategory,
  useIncidentCategoryOptions,
} from 'components/IncidentConfig/incidentCategorySession';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { parseError } from 'hooks/shared/error';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import type {
  PlanLibraryFile,
  PlanLibraryIncidentTags,
  PlanLibraryLayer,
  PlanLibraryPlan,
} from 'hooks/planLibrary/planLibraryHooks';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { HWCG_COMPANY_OPTIONS } from 'components/IncidentConfig/CreateIncident/hwcgIncidentSpecifics';
import {
  useFilledRosterTemplates,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import { useHwcgFlag } from 'utils/featureFlags';
import { selectPlanRosterTemplate } from './planLibrarySteps';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';
import {
  PlanRosterDialog,
  type PlanRosterDialogSeed,
} from './PlanRosterDialog';

const COMPANY_OPTIONS = HWCG_COMPANY_OPTIONS.map((company) => ({
  value: company,
  label: company,
}));

export const PLAN_LIBRARY_FILES_FOLDER = 'Plan Library';

function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
      <div>
        <div className="tw:text-sm tw:font-medium">{title}</div>
        <p className="tw:mt-0.5 tw:text-xs tw:text-muted-foreground">
          {description}
        </p>
      </div>
      {action}
    </div>
  );
}

function isFile(row: { type: string }): row is NotebookFile {
  return row.type === 'file';
}

export function PlanDocumentsSection({
  plan,
  busy,
  onChange,
}: {
  plan: PlanLibraryPlan;
  busy: boolean;
  onChange: (files: PlanLibraryFile[]) => void;
}) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useGetAllFiles(groupGuid ?? undefined, 'organization');
  const rows = useMemo(() => data?.dataset ?? [], [data]);
  const orgFiles = useMemo(() => rows.filter(isFile), [rows]);
  const fileById = useMemo(
    () => new Map(orgFiles.map((file) => [file.id as string, file])),
    [orgFiles]
  );
  const options = useMemo(() => {
    const listed = orgFiles.map((file) => ({
      value: file.id as string,
      label: file.file_name,
    }));
    const known = new Set(listed.map((option) => option.value));
    const missing = plan.files
      .filter((file) => !known.has(file.fileId))
      .map((file) => ({ value: file.fileId, label: file.name }));
    return [...listed, ...missing];
  }, [orgFiles, plan.files]);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      if (!groupGuid) throw new Error('No organization selected');
      const existing = rows.find(
        (row) =>
          row.type === 'folder' &&
          !row.notefolderId &&
          row.folder_name === PLAN_LIBRARY_FILES_FOLDER
      );
      const folderId =
        existing?.id ??
        (
          await axios.post<{ id: string }>(apiConfig.apiGateway.foldersBase, {
            name: PLAN_LIBRARY_FILES_FOLDER,
            workspaceId: groupGuid,
            scopeType: 'organization',
          })
        ).data?.id;
      return uploadFilesToBackend({
        files,
        workspaceId: groupGuid,
        notefolderId: folderId ?? null,
        scopeType: 'organization',
      });
    },
    onSuccess: (uploaded) => {
      void queryClient.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(groupGuid ?? undefined),
        exact: false,
      });
      const added = uploaded
        .filter((file) => file.id)
        .map((file) => ({
          fileId: file.id as string,
          name: file.file_name || 'Untitled file',
        }));
      const known = new Set(plan.files.map((file) => file.fileId));
      onChange([
        ...plan.files,
        ...added.filter((file) => !known.has(file.fileId)),
      ]);
    },
    onError: (error) =>
      toast.error(parseError(error) || 'Could not upload the files'),
  });

  const openFile = async (fileId: string) => {
    const file = fileById.get(fileId);
    if (!file?.fileUrl) {
      toast.error('That file is no longer in the organization library');
      return;
    }
    try {
      window.open(
        await fetchDownloadSignedUrl(file.fileUrl),
        '_blank',
        'noopener'
      );
    } catch (error) {
      toast.error(parseError(error) || 'Could not open the file');
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <SectionHeader
        title="Documents"
        description="Files from the organization library. Applying the plan copies them into the workspace Files."
        action={
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="tw:hidden"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []).slice(0, 10);
                event.target.value = '';
                if (files.length > 0) upload.mutate(files);
              }}
            />
            <Button
              type="button"
              size="sm"
              disabled={busy || upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="tw:size-4" />
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        }
      />
      <MultiIdCombobox
        options={options}
        value={plan.files.map((file) => file.fileId)}
        disabled={busy}
        placeholder="Add files from the organization library"
        onChange={(ids) =>
          onChange(
            ids.map((id) => {
              const current = plan.files.find((file) => file.fileId === id);
              return (
                current ?? {
                  fileId: id,
                  name: fileById.get(id)?.file_name ?? 'Untitled file',
                }
              );
            })
          )
        }
      />
      {plan.files.length > 0 ? (
        <ul className="tw:rounded-md tw:border tw:border-border">
          {plan.files.map((file) => (
            <li
              key={file.fileId}
              className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-2 tw:py-1.5 tw:last:border-b-0"
            >
              <button
                type="button"
                className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:text-left tw:text-sm"
                onClick={() => void openFile(file.fileId)}
              >
                <span className="tw:truncate tw:font-medium">{file.name}</span>
                <ExternalLink className="tw:size-3 tw:shrink-0 tw:text-muted-foreground" />
              </button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove ${file.name} from the plan`}
                disabled={busy}
                onClick={() =>
                  onChange(
                    plan.files.filter((entry) => entry.fileId !== file.fileId)
                  )
                }
              >
                <Trash2 className="tw:size-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function structureNodesForFilled(
  template: FilledRosterTemplate,
  libraryEntries: { id: string; nodes: RosterTemplateNodeDto[] }[],
  catalog: { slug: string; nodes?: RosterTemplateNodeDto[] }[]
): RosterTemplateNodeDto[] {
  if (template.sourceTemplateId) {
    return (
      libraryEntries.find((entry) => entry.id === template.sourceTemplateId)
        ?.nodes ?? []
    );
  }
  if (template.sourceSlug) {
    return (
      catalog.find((entry) => entry.slug === template.sourceSlug)?.nodes ?? []
    );
  }
  return [];
}

export function PlanRosterTemplatesSection({
  plan,
  busy,
  onChange,
}: {
  plan: PlanLibraryPlan;
  busy: boolean;
  onChange: (next: {
    rosterTemplateIds?: string[];
    filledRosterTemplateId?: string | null;
  }) => void;
}) {
  const hwcg = useHwcgFlag();
  const { data: templates = [] } = useRosterTemplateLibrary();
  const catalog = useRosterTemplatesCatalog();
  const filledTemplates = useFilledRosterTemplates({ enabled: hwcg });
  const [dialogSeed, setDialogSeed] = useState<PlanRosterDialogSeed | null>(
    null
  );

  const rows = useMemo(() => {
    const empty = templates
      .filter((template) => !template.attachOnly)
      .map((template) => ({
        key: `empty:${template.id}`,
        id: template.id,
        name: template.name,
        kind: 'empty' as const,
        selected: plan.rosterTemplateIds.includes(template.id),
        detail: 'Empty',
        nodes: template.nodes,
        filled: null as FilledRosterTemplate | null,
      }));
    const knownEmpty = new Set(empty.map((row) => row.id));
    const removedEmpty = plan.rosterTemplateIds
      .filter((id) => !knownEmpty.has(id))
      .map((id) => ({
        key: `empty:${id}`,
        id,
        name: 'Removed template',
        kind: 'empty' as const,
        selected: true,
        detail: 'Empty',
        nodes: [] as RosterTemplateNodeDto[],
        filled: null as FilledRosterTemplate | null,
      }));
    const filled = hwcg
      ? (filledTemplates.data ?? []).map((template) => ({
          key: `filled:${template.id}`,
          id: template.id,
          name: template.name,
          kind: 'filled' as const,
          selected: plan.filledRosterTemplateId === template.id,
          detail: filledTemplateDetail(template),
          nodes: [] as RosterTemplateNodeDto[],
          filled: template,
        }))
      : [];
    const knownFilled = new Set(filled.map((row) => row.id));
    const removedFilled =
      hwcg &&
      plan.filledRosterTemplateId &&
      !knownFilled.has(plan.filledRosterTemplateId)
        ? [
            {
              key: `filled:${plan.filledRosterTemplateId}`,
              id: plan.filledRosterTemplateId,
              name: 'Removed filled template',
              kind: 'filled' as const,
              selected: true,
              detail: 'Filled',
              nodes: [] as RosterTemplateNodeDto[],
              filled: null as FilledRosterTemplate | null,
            },
          ]
        : [];
    return [...empty, ...removedEmpty, ...filled, ...removedFilled].sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }, [
    filledTemplates.data,
    hwcg,
    plan.filledRosterTemplateId,
    plan.rosterTemplateIds,
    templates,
  ]);

  const openRow = (row: (typeof rows)[number]) => {
    if (row.kind === 'filled' && row.filled) {
      setDialogSeed({
        existingFilled: row.filled,
        structureNodes: structureNodesForFilled(
          row.filled,
          templates,
          catalog.data?.templates ?? []
        ),
        templateName: row.filled.name,
        sourceTemplateId: row.filled.sourceTemplateId,
        sourceSlug: row.filled.sourceSlug,
      });
      return;
    }
    setDialogSeed({
      templateName: row.name === 'Removed template' ? '' : row.name,
      sourceTemplateId: row.kind === 'empty' ? row.id : null,
      sourceSlug: row.kind === 'empty' ? `org:${row.id}` : null,
      structureNodes: row.nodes,
    });
  };

  const toggleRow = (row: (typeof rows)[number], checked: boolean) => {
    onChange(
      selectPlanRosterTemplate(
        {
          rosterTemplateIds: plan.rosterTemplateIds,
          filledRosterTemplateId: plan.filledRosterTemplateId,
        },
        { kind: row.kind, id: row.id },
        checked
      )
    );
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <SectionHeader
        title="Roster templates"
        description={
          hwcg
            ? 'Every saved roster template. A plan holds one. Selecting one replaces whatever is already attached, and opening it assigns people or assets.'
            : 'Structure-only templates. A plan holds one roster template. Selecting one replaces whatever is already attached.'
        }
        action={
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => setDialogSeed({})}
          >
            <Network className="tw:size-4" />
            New template
          </Button>
        }
      />
      {rows.length === 0 ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          No roster templates saved yet.
        </p>
      ) : (
        <ul className="tw:rounded-md tw:border tw:border-border">
          {rows.map((row) => (
            <li
              key={row.key}
              className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-2 tw:py-1.5 tw:last:border-b-0"
            >
              <Checkbox
                aria-label={`Select ${row.name}`}
                checked={row.selected}
                disabled={busy}
                onCheckedChange={(checked) => toggleRow(row, checked === true)}
              />
              <button
                type="button"
                className="tw:min-w-0 tw:flex-1 tw:bg-transparent tw:p-0 tw:text-left tw:text-sm tw:break-words"
                onClick={() => openRow(row)}
              >
                {row.name}
                <span className="tw:ml-2 tw:text-xs tw:text-muted-foreground">
                  {row.detail}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {dialogSeed ? (
        <PlanRosterDialog
          planId={plan.id}
          planName={plan.name}
          seed={dialogSeed}
          onAddTemplate={(templateId) => {
            onChange(
              selectPlanRosterTemplate(
                {
                  rosterTemplateIds: plan.rosterTemplateIds,
                  filledRosterTemplateId: plan.filledRosterTemplateId,
                },
                { kind: 'empty', id: templateId },
                true
              )
            );
          }}
          onFilledTemplate={(templateId) => {
            onChange(
              selectPlanRosterTemplate(
                {
                  rosterTemplateIds: plan.rosterTemplateIds,
                  filledRosterTemplateId: plan.filledRosterTemplateId,
                },
                { kind: 'filled', id: templateId },
                true
              )
            );
          }}
          onClose={() => setDialogSeed(null)}
        />
      ) : null}
    </div>
  );
}

function filledTemplateDetail(template: FilledRosterTemplate) {
  const people = template.assignments.length;
  const assets = template.resources?.length ?? 0;
  const parts = [`${people} ${people === 1 ? 'person' : 'people'}`];
  if (assets > 0) {
    parts.push(`${assets} ${assets === 1 ? 'asset' : 'assets'}`);
  }
  return parts.join(', ');
}

export function layerKey(layer: Pick<PlanLibraryLayer, 'kind' | 'id'>) {
  return `${layer.kind}:${layer.id}`;
}

export function PlanLayersSection({
  plan,
  busy,
  onChange,
}: {
  plan: PlanLibraryPlan;
  busy: boolean;
  onChange: (layers: PlanLibraryLayer[]) => void;
}) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const internalQuery = useListInternalLayersQuery({
    filter: { groupGuid: groupGuid ?? '' },
    perPage: 200,
    include: ['incident'],
  });
  const externalQuery = useListExternalLayersQuery(
    {
      filter: { groupGuid: groupGuid ?? '' },
      perPage: 200,
      include: ['incident'],
    },
    { enabled: Boolean(groupGuid) }
  );

  const available = useMemo<PlanLibraryLayer[]>(() => {
    const internal = (internalQuery.data?.data ?? [])
      .filter((layer) => !layer.incidentId && !layer.deletedAt)
      .map((layer) => ({
        kind: 'internal' as const,
        id: layer.id as string,
        name: layer.name,
      }));
    const external = (externalQuery.data?.data ?? [])
      .filter((layer) => !layer.incidentId)
      .map((layer) => ({
        kind: 'external' as const,
        id: layer.id as string,
        name: layer.name,
      }));
    return [...internal, ...external].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [internalQuery.data?.data, externalQuery.data?.data]);

  const byKey = useMemo(() => {
    const map = new Map<string, PlanLibraryLayer>();
    for (const layer of [...plan.layers, ...available]) {
      map.set(layerKey(layer), layer);
    }
    return map;
  }, [plan.layers, available]);

  const options = useMemo(
    () =>
      [...byKey.values()].map((layer) => ({
        value: layerKey(layer),
        label: `${layer.name} (${layer.kind === 'internal' ? 'Internal' : 'External'})`,
      })),
    [byKey]
  );

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <SectionHeader
        title="Map layers"
        description="Suggested layers for this plan. Applying the plan lists them and leaves the map unchanged."
      />
      <MultiIdCombobox
        options={options}
        value={plan.layers.map(layerKey)}
        disabled={busy}
        placeholder="Select internal or external layers"
        onChange={(keys) =>
          onChange(
            keys
              .map((key) => byKey.get(key))
              .filter((layer): layer is PlanLibraryLayer => Boolean(layer))
          )
        }
      />
    </div>
  );
}

export function PlanIncidentTagsSection({
  plan,
  busy,
  onChange,
}: {
  plan: PlanLibraryPlan;
  busy: boolean;
  onChange: (tags: PlanLibraryIncidentTags) => void;
}) {
  const aorText = useAorText();
  const { data: aors = [] } = useAors();
  const categoryOptions = useIncidentCategoryOptions(plan.incidentCategories);
  const aorOptions = useMemo(() => {
    const listed = aors.map((aor) => ({ value: aor.id, label: aor.name }));
    const known = new Set(listed.map((option) => option.value));
    const missing = plan.aorIds
      .filter((id) => !known.has(id))
      .map((id) => ({ value: id, label: aorText('Removed AOR') }));
    return [...listed, ...missing];
  }, [aors, plan.aorIds, aorText]);
  const save = (incidentCategories: string[], aorIds: string[]) =>
    onChange({
      useForIncidents: incidentCategories.length > 0 || aorIds.length > 0,
      incidentCategories,
      aorIds,
    });

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <SectionHeader
        title="Use in case of incident"
        description={aorText(
          'Matching plans are suggested first when a workspace shares a category or AOR.'
        )}
      />
      <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:md:grid-cols-2">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <Label>Incident categories</Label>
          <MultiIdCombobox
            options={categoryOptions}
            value={plan.incidentCategories}
            disabled={busy}
            creatable
            createLabel="Add incident category"
            onCreate={addSessionIncidentCategory}
            placeholder="Any category"
            onChange={(incidentCategories) =>
              save(incidentCategories, plan.aorIds)
            }
          />
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1">
          <Label>{aorText('Areas of responsibility')}</Label>
          <MultiIdCombobox
            options={aorOptions}
            value={plan.aorIds}
            disabled={busy}
            placeholder={aorText('Any AOR')}
            onChange={(aorIds) => save(plan.incidentCategories, aorIds)}
          />
        </div>
      </div>
    </div>
  );
}

/** HWCG-only: tag a plan to a Responsible Party for create-incident Apply Plan. */
export function PlanHwcgTagsSection({
  plan,
  busy,
  onChange,
}: {
  plan: PlanLibraryPlan;
  busy: boolean;
  onChange: (next: { responsiblePartyCompany: string | null }) => void;
}) {
  const hwcg = useHwcgFlag();
  if (!hwcg) return null;

  const company = plan.responsiblePartyCompany?.trim() || null;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <SectionHeader
        title="HWCG create incident"
        description="Tag this plan to a Responsible Party so it appears on Apply Plan. Attach empty or filled roster templates in the Roster Template step."
      />
      <div className="tw:flex tw:flex-col tw:gap-1">
        <Label htmlFor={`plan-hwcg-rp-${plan.id}`}>Responsible Party</Label>
        <SearchableSelect
          id={`plan-hwcg-rp-${plan.id}`}
          aria-label="Responsible Party"
          options={COMPANY_OPTIONS}
          value={company ? { value: company, label: company } : null}
          placeholder="Any (untagged)"
          clearable
          contentClassName="dark"
          positionerClassName="tw:z-[60]"
          disabled={busy}
          onChange={(next) =>
            onChange({
              responsiblePartyCompany: next?.value ?? null,
            })
          }
        />
      </div>
    </div>
  );
}
